import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, ShieldIcon, FingerprintIcon, ScanFaceIcon } from 'lucide-react';
export type MPINMode = 'enter' | 'set' | 'confirm' | 'success';
interface MPINScreenProps {
  mode: MPINMode;
  onComplete: () => void;
  onBack: () => void;
  onModeChange?: (mode: MPINMode) => void;
}
export function MPINScreen({
  mode,
  onComplete,
  onBack,
  onModeChange
}: MPINScreenProps) {
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [error, setError] = useState(false);
  const [biometricPhase, setBiometricPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [biometricType, setBiometricType] = useState<'face' | 'thumb'>('thumb');
  const handleBiometricAuth = (type: 'face' | 'thumb') => {
    setBiometricType(type);
    setBiometricPhase('scanning');
    // Simulate scanning
    setTimeout(() => {
      setBiometricPhase('verified');
      // Auto-complete after verified
      setTimeout(() => {
        setBiometricPhase('idle');
        if (mode === 'enter') {
          onComplete();
        }
      }, 800);
    }, 2000);
  };
  // Handle PIN entry
  const handleNumberClick = (num: string) => {
    if (pin.length < 4) {
      setPin(prev => prev + num);
      setError(false);
    }
  };
  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError(false);
  };
  // Auto-submit when 4 digits are entered
  useEffect(() => {
    if (pin.length === 4) {
      const timer = setTimeout(() => {
        if (mode === 'enter') {
          onComplete();
        } else if (mode === 'set') {
          setFirstPin(pin);
          setPin('');
          if (onModeChange) onModeChange('confirm');
        } else if (mode === 'confirm') {
          if (pin === firstPin) {
            if (onModeChange) onModeChange('success');
          } else {
            setError(true);
            setPin('');
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pin, mode, firstPin, onComplete, onModeChange]);
  // Handle success screen timeout
  useEffect(() => {
    if (mode === 'success') {
      const timer = setTimeout(() => {
        onComplete();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [mode, onComplete]);
  const getTitle = () => {
    switch (mode) {
      case 'enter':
        return 'Enter MPIN';
      case 'set':
        return 'Set MPIN';
      case 'confirm':
        return 'Confirm MPIN';
      case 'success':
        return 'Success!';
      default:
        return 'MPIN';
    }
  };
  const getSubtitle = () => {
    switch (mode) {
      case 'enter':
        return 'Enter your 4-digit MPIN to continue';
      case 'set':
        return 'Create a 4-digit MPIN for secure access';
      case 'confirm':
        return 'Re-enter your MPIN to confirm';
      case 'success':
        return 'Your MPIN has been set successfully';
      default:
        return '';
    }
  };
  if (mode === 'success') {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="w-full min-h-screen flex flex-col items-center justify-center py-6 px-6" style={{
      backgroundColor: '#FFF5F0'
    }} data-id="element-404">
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        damping: 15,
        stiffness: 200
      }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6" data-id="element-405">
          <CheckCircle2Icon className="w-14 h-14 text-[#22C55E]" strokeWidth={1.5} data-id="element-406" />
        </motion.div>
        <motion.h1 initial={{
        y: 10,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-[28px] font-bold text-[#1A1A1A] text-center mb-2" data-id="element-407">
          Congratulations!
        </motion.h1>
        <motion.p initial={{
        y: 10,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="text-[15px] text-[#8A7B72] text-center max-w-[280px]" data-id="element-408">
          Your MPIN has been set successfully. You can now use it to login
          securely.
        </motion.p>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full min-h-screen flex flex-col py-6 px-6" style={{
    backgroundColor: '#FFF5F0'
  }} data-id="element-409">
      {/* Header */}
      <div className="flex items-center justify-between mb-12" data-id="element-410">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors" data-id="element-411">
          <ArrowLeftIcon className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2} data-id="element-412" />
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-8 w-auto" data-id="element-413" />
        <div className="w-10" data-id="element-414" />
      </div>

      {/* Title */}
      <div className="flex flex-col items-center mb-12 text-center" data-id="element-415">
        <div className="w-16 h-16 rounded-full bg-[#FCECE3] flex items-center justify-center mb-6" data-id="element-416">
          <ShieldIcon className="w-8 h-8 text-[#D95A1A]" strokeWidth={1.5} data-id="element-417" />
        </div>
        <h1 className="text-[28px] font-bold text-[#1A1A1A] leading-tight mb-2 tracking-tight" data-id="element-418">
          {getTitle()}
        </h1>
        <p className={`text-[15px] ${error ? 'text-red-500 font-medium' : 'text-[#8A7B72]'}`} data-id="element-419">
          {error ? 'PINs do not match. Try again.' : getSubtitle()}
        </p>
      </div>

      {/* PIN Indicators */}
      <div className="flex justify-center gap-4 mb-8" data-id="element-420">
        {[0, 1, 2, 3].map(index => <div key={index} className={`w-4 h-4 rounded-full transition-all duration-200 ${index < pin.length ? 'bg-[#D95A1A] scale-110' : 'bg-[#FCECE3] border-2 border-[#E8D5CB]'} ${error ? 'bg-red-500 border-red-500' : ''}`} data-id="element-421" />)}
      </div>

      {/* Biometric Options - only show in 'enter' mode */}
      {mode === 'enter' && <div className="flex flex-col items-center mb-8" data-id="element-422">
          <AnimatePresence mode="wait" data-id="element-423">
            {biometricPhase === 'idle' && <motion.div key="idle" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} className="flex flex-col items-center gap-3" data-id="element-424">
                <p className="text-[12px] font-bold text-[#8A7B72] uppercase tracking-wider mb-2" data-id="element-425">
                  Or verify with
                </p>
                <div className="flex items-center gap-5" data-id="element-426">
                  <button onClick={() => handleBiometricAuth('thumb')} className="flex flex-col items-center gap-2 group" data-id="element-427">
                    <div className="w-14 h-14 rounded-full bg-[#FCECE3] border-2 border-[#E8D5CB] flex items-center justify-center group-hover:border-[#D95A1A] group-hover:bg-[#FDE8DB] transition-all group-active:scale-95" data-id="element-428">
                      <FingerprintIcon className="w-7 h-7 text-[#D95A1A]" strokeWidth={1.5} data-id="element-429" />
                    </div>
                    <span className="text-[11px] font-semibold text-[#8A7B72]" data-id="element-430">
                      Fingerprint
                    </span>
                  </button>
                  <div className="w-px h-12 bg-[#E8D5CB]" data-id="element-431" />
                  <button onClick={() => handleBiometricAuth('face')} className="flex flex-col items-center gap-2 group" data-id="element-432">
                    <div className="w-14 h-14 rounded-full bg-[#FCECE3] border-2 border-[#E8D5CB] flex items-center justify-center group-hover:border-[#D95A1A] group-hover:bg-[#FDE8DB] transition-all group-active:scale-95" data-id="element-433">
                      <ScanFaceIcon className="w-7 h-7 text-[#D95A1A]" strokeWidth={1.5} data-id="element-434" />
                    </div>
                    <span className="text-[11px] font-semibold text-[#8A7B72]" data-id="element-435">
                      Face Lock
                    </span>
                  </button>
                </div>
              </motion.div>}

            {biometricPhase === 'scanning' && <motion.div key="scanning" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex flex-col items-center gap-3" data-id="element-436">
                <motion.div animate={{
            scale: [1, 1.15, 1],
            opacity: [0.7, 1, 0.7]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-16 h-16 rounded-full bg-[#D95A1A]/15 flex items-center justify-center" data-id="element-437">
                  {biometricType === 'thumb' ? <FingerprintIcon className="w-8 h-8 text-[#D95A1A]" strokeWidth={1.5} data-id="element-438" /> : <ScanFaceIcon className="w-8 h-8 text-[#D95A1A]" strokeWidth={1.5} data-id="element-439" />}
                </motion.div>
                <p className="text-[13px] font-semibold text-[#D95A1A]" data-id="element-440">
                  {biometricType === 'thumb' ? 'Scanning fingerprint...' : 'Scanning face...'}
                </p>
              </motion.div>}

            {biometricPhase === 'verified' && <motion.div key="verified" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="flex flex-col items-center gap-3" data-id="element-441">
                <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200
          }} className="w-16 h-16 rounded-full bg-[#22C55E]/15 flex items-center justify-center" data-id="element-442">
                  <CheckCircle2Icon className="w-8 h-8 text-[#22C55E]" strokeWidth={1.5} data-id="element-443" />
                </motion.div>
                <p className="text-[13px] font-semibold text-[#22C55E]" data-id="element-444">
                  Verified successfully!
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>}

      {/* Numpad */}
      <div className="mt-auto pb-8" data-id="element-445">
        <div className="grid grid-cols-3 gap-y-6 gap-x-4 max-w-[280px] mx-auto" data-id="element-446">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => handleNumberClick(num.toString())} disabled={biometricPhase !== 'idle'} className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-[28px] font-medium text-[#1A1A1A] hover:bg-[#FCECE3] active:bg-[#F5DDD1] transition-colors disabled:opacity-40 disabled:pointer-events-none" data-id="element-447">
              {num}
            </button>)}
          <div data-id="element-448" /> {/* Empty space */}
          <button onClick={() => handleNumberClick('0')} disabled={biometricPhase !== 'idle'} className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-[28px] font-medium text-[#1A1A1A] hover:bg-[#FCECE3] active:bg-[#F5DDD1] transition-colors disabled:opacity-40 disabled:pointer-events-none" data-id="element-449">
            0
          </button>
          <button onClick={handleDelete} disabled={biometricPhase !== 'idle'} className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-[#1A1A1A] hover:bg-[#FCECE3] active:bg-[#F5DDD1] transition-colors disabled:opacity-40 disabled:pointer-events-none" data-id="element-450">
            <ArrowLeftIcon className="w-7 h-7" strokeWidth={2} data-id="element-451" />
          </button>
        </div>
      </div>
    </motion.div>;
}